import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import { Button } from "../../../components/ui/Button";
import type { OnboardingFlow } from "../useOnboardingFlow";

type RegistryFooterFlow = Pick<
  OnboardingFlow,
  | "registryPrimaryAction"
  | "registryState"
  | "confirmRegistryProfile"
  | "stpsSearch"
  | "searchRepsAgain"
  | "refineBigSearch"
  | "submitRegistryId"
  | "handleRegistryManualExit"
>;

export function RegistryFooterActions({
  flow,
  variant,
}: {
  flow: RegistryFooterFlow;
  /**
   * `desktop` follows the desktop screens (Figma "D7.identite"): one button,
   * "Continue" while the registry is searched and on the profile it found,
   * "Verify" on a typed number. The manual way out stays where those screens
   * have no design (the STPS and BIG searches), and on the profile found by
   * name, which the design draws without it: it is the only way to say "this
   * is not me", since Back runs the same lookup again and finds the same
   * record.
   */
  variant: "mobile" | "desktop";
}): React.JSX.Element {
  const { t } = useTranslation();
  const registryAction = flow.registryPrimaryAction;
  if (registryAction === null)
    throw new Error("Registry footer requires an action");
  const primaryLabel = (() => {
    switch (registryAction.kind) {
      case "loading":
        if (variant === "desktop") return t("Continue");
        return registryAction.registry === "reps"
          ? t("Searching REPS...")
          : registryAction.registry === "stps"
            ? t("Searching STPS...")
            : registryAction.registry === "big"
              ? t("Searching BIG-register...")
              : t("Searching the registry...");
      case "resolving":
        return t("Checking your profile...");
      case "confirm":
        return variant === "desktop" ? t("Continue") : t("Yes, this is me");
      case "search":
        return registryAction.registry === "stps"
          ? t("Search STPS")
          : registryAction.registry === "reps"
            ? t("Search REPS")
            : t("Search BIG-register again");
      case "identifier":
        return registryAction.registry === "stps"
          ? t("Search by Autorisations-ID")
          : registryAction.registry === "big"
            ? t("Search BIG number")
            : variant === "desktop"
              ? t("Verify")
              : t("Continue with this number");
      case "manual":
        return t("Fill in my profile manually");
      default:
        throw new Error(registryAction satisfies never);
    }
  })();
  const manualVia =
    flow.registryState !== null &&
    (flow.registryState.status === "found" ||
      (flow.registryState.status === "multiple" &&
        !flow.registryState.truncated))
      ? "rejected"
      : "manual";
  const showsManualExit =
    variant === "mobile"
      ? registryAction.kind !== "manual"
      : registryAction.kind === "confirm" ||
        registryAction.kind === "search" ||
        (registryAction.kind === "identifier" &&
          (registryAction.registry === "stps" ||
            registryAction.registry === "big"));
  return (
    <>
      <Button
        variant="primary"
        size={variant === "mobile" ? "lg" : "md"}
        className="w-full"
        disabled={
          registryAction.kind === "loading" ||
          registryAction.kind === "resolving" ||
          ((registryAction.kind === "search" ||
            registryAction.kind === "identifier") &&
            registryAction.disabled)
        }
        leftIcon={
          (registryAction.kind === "loading" && variant === "mobile") ||
          registryAction.kind === "resolving" ? (
            <IconLoader2 className="animate-spin" />
          ) : null
        }
        onClick={() => {
          switch (registryAction.kind) {
            case "loading":
            case "resolving":
              return;
            case "confirm":
              flow.confirmRegistryProfile(registryAction.profile);
              return;
            case "search":
              if (registryAction.registry === "stps")
                flow.stpsSearch.onSearch();
              else if (registryAction.registry === "reps")
                flow.searchRepsAgain();
              else flow.refineBigSearch();
              return;
            case "identifier":
              flow.submitRegistryId();
              return;
            case "manual":
              flow.handleRegistryManualExit(registryAction.via);
              return;
            default:
              throw new Error(registryAction satisfies never);
          }
        }}
      >
        {primaryLabel}
      </Button>
      {showsManualExit && (
        <Button
          variant="ghost"
          size={variant === "mobile" ? "lg" : "md"}
          className="w-full"
          onClick={() => {
            flow.handleRegistryManualExit(manualVia);
          }}
        >
          {t("Fill in my profile manually")}
        </Button>
      )}
    </>
  );
}
